/**
 * useMedsheetLookup - Hook for medsheet lookup and interaction
 * 
 * Provides functionality to:
 * - Check if a drug has medsheets
 * - Get medsheet info with name difference detection
 * - Handle opening sheets (single or multiple)
 */

import { useState, useCallback } from 'react';
import {
  lookupDrug,
  hasMedsheet as checkHasMedsheet,
  getSheetUrl,
} from '@healthdesk/shared-services';

/**
 * useMedsheetLookup Hook
 * 
 * @returns {Object} Hook API
 */
const useMedsheetLookup = () => {
  // State for menu (when multiple sheets available)
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuSheets, setMenuSheets] = useState([]);
  const [menuNameDiff, setMenuNameDiff] = useState(null);

  /**
   * Quick synchronous check if a drug has medsheets
   * @param {string} drugName - Name to check
   * @returns {boolean} True if drug has medsheets
   */
  const hasMedsheet = useCallback((drugName) => {
    return checkHasMedsheet(drugName);
  }, []);

  /**
   * Get full medsheet info for a drug
   * @param {string} drugName - Name to look up
   * @returns {Object} { sheets, hasNameDifference, searchedName, matchedName }
   */
  const getMedsheetInfo = useCallback((drugName) => {
    return lookupDrug(drugName);
  }, []);

  /**
   * Handle medsheet icon click
   * Opens single sheet directly, or shows menu for multiple
   * @param {string} drugName - Drug name
   * @param {Event} event - Click event (for menu anchor)
   */
  const handleMedsheetClick = useCallback((drugName, event) => {
    const info = lookupDrug(drugName);
    
    if (info.sheets.length === 0) {
      // No sheets available (shouldn't happen if indicator is shown correctly)
      console.warn('[useMedsheetLookup] No sheets found for:', drugName);
      return;
    }

    if (info.sheets.length === 1) {
      // Single sheet: Open directly in new tab
      const url = getSheetUrl(info.sheets[0].file);
      window.open(url, '_blank');
    } else {
      // Multiple sheets: Show selection menu
      setMenuAnchorEl(event.currentTarget);
      setMenuSheets(info.sheets);
      setMenuNameDiff(info.hasNameDifference ? {
        searched: info.searchedName,
        matched: info.matchedName,
      } : null);
    }
  }, []);

  /**
   * Handle sheet selection from menu
   * @param {Object} sheet - Selected sheet { file, title, displayName }
   */
  const handleSheetSelect = useCallback((sheet) => {
    const url = getSheetUrl(sheet.file);
    window.open(url, '_blank');
    closeMenu();
  }, []);

  /**
   * Close the menu
   */
  const closeMenu = useCallback(() => {
    setMenuAnchorEl(null);
    setMenuSheets([]);
    setMenuNameDiff(null);
  }, []);

  /**
   * Check if menu is open
   */
  const isMenuOpen = Boolean(menuAnchorEl);

  return {
    // Quick lookup
    hasMedsheet,
    getMedsheetInfo,
    
    // Click handling
    handleMedsheetClick,
    
    // Menu state
    menuAnchorEl,
    menuSheets,
    menuNameDiff,
    isMenuOpen,
    handleSheetSelect,
    closeMenu,
  };
};

export default useMedsheetLookup;
